import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Heart, ShoppingBag, Store } from 'lucide-react';
import { SHOPPER_HOME } from '../../constants/routes';
import { useCartContext } from '../../hooks/useCart';
import { useFavoritesContext } from '../../hooks/useFavorites';

const TABS = [
  { id: 'explore', label: 'Explore', icon: Home, path: SHOPPER_HOME },
  { id: 'shop', label: 'Shop', icon: Store, path: '/boutiques' },
  { id: 'likes', label: 'Likes', icon: Heart, path: '/likes' },
  { id: 'bag', label: 'Bag', icon: ShoppingBag, path: '/bag' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function activeTabFor(pathname: string): TabId | null {
  if (pathname === SHOPPER_HOME) return 'explore';
  if (pathname.startsWith('/boutique')) return 'shop';
  if (pathname.startsWith('/likes')) return 'likes';
  if (pathname.startsWith('/bag')) return 'bag';
  return null;
}

/**
 * Primary shopper tab bar. The active tab comes from the URL rather than local
 * state so it stays correct after back/forward navigation and deep links.
 *
 * Mobile only — desktop surfaces navigate via the header and footer.
 */
export function ShopperBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const cart = useCartContext();
  const favorites = useFavoritesContext();

  const activeTab = activeTabFor(location.pathname);

  const badgeFor = (id: TabId) => {
    if (id === 'bag') return cart.itemCount;
    if (id === 'likes') return favorites.favoriteCount;
    return 0;
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
      <div className="flex items-center justify-around py-2 pb-[env(safe-area-inset-bottom)]">
        {TABS.map(({ id, label, icon: Icon, path }) => {
          const isActive = activeTab === id;
          const badge = badgeFor(id);
          return (
            <button
              key={id}
              onClick={() => navigate(path)}
              aria-current={isActive ? 'page' : undefined}
              className={`relative flex flex-col items-center py-2 px-4 transition-colors ${
                isActive ? 'text-primary' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'fill-primary/10' : ''}`} />
              {badge > 0 && (
                <span className="absolute top-1 right-2 h-4 min-w-[16px] px-1 flex items-center justify-center bg-primary text-white text-[10px] font-bold rounded-full">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
              <span className="text-xs mt-1">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
